import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Icon, Link, Modal, SkeletonLoader, Title, Text } from '@vmos2/design-system';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';
import { orderApi, getPrefixedRoute } from '@vmos2/core';
import { useLoyaltyContext, useUser } from '@vmos2/providers';
import { isAppleDevice } from '@vmos2/utils';
import { useTheme } from '@emotion/react';

export type OrderClaimProps = {
  appDownloadLink?: {
    ios?: string,
    android?: string,
  },
  closeRoute: string,
  locale: string,
  navigateTo: (route: string, params?: Record<string, string>) => void,
  orderUUID?: string,
  path: string,
};

type ContentProps = {
  isLoading: boolean,
  error: string | null,
  appLink?: string | null,
  locale: string,
  os: string | null,
};

export const OrderClaimScreen = ({
  appDownloadLink,
  closeRoute,
  locale,
  navigateTo,
  orderUUID,
  path,
}: OrderClaimProps) => {
  const showOrderClaimScreen = path?.startsWith('/claim');
  const [isLoading, setIsLoading] = useState(true);
  const [disableTransitions, setDisableTransitions] = useState(true);
  const os = isAppleDevice() ? 'ios' : navigator.userAgent?.match(/Android/i) ? 'android' : null;
  const appLink = os && appDownloadLink?.[os];
  const [error, setError] = useState<string | null>(null);
  const hasAttemptedOrderClaim = useRef(false);
  const { isLoadingLoyalty } = useLoyaltyContext('General');
  const { pollUpdatePoints } = useLoyaltyContext('Points');
  const { isLoggedIn } = useUser();

  const enableTransitions = () => {
    setDisableTransitions(false);
  };

  useEffect(() => {
    if (!orderUUID) {
      setError('error');
      setIsLoading(false);
      return;
    }

    if (!isLoggedIn) {
      navigateTo(getPrefixedRoute(`account.auth.login`), {
        fromRoute: 'preorder.claim',
        fromParams: JSON.stringify({ orderUUID }),
      });

      return;
    }

    if (isLoadingLoyalty) {
      return;
    }

    if (!hasAttemptedOrderClaim.current) {
      hasAttemptedOrderClaim.current = true;

      orderApi
        .post(`commands/claim/${orderUUID}`)
        .then(() => pollUpdatePoints())
        .then(() => setError(null))
        .catch(e => setError(e?.message ?? e?.data?.message ?? 'error'))
        .finally(() => setIsLoading(false));
    }
  }, [isLoadingLoyalty, isLoggedIn, navigateTo, orderUUID, pollUpdatePoints]);

  const closeModal = useCallback(() => {
    if (isLoading) {
      return;
    }

    navigateTo(closeRoute);
  }, [navigateTo, closeRoute, isLoading]);

  return (
    <Modal
      disableTransitions={disableTransitions}
      height="100%"
      isFullScreen
      isOpen={showOrderClaimScreen}
      onAfterOpen={enableTransitions}
      onRequestClose={closeModal}
    >
      <Content appLink={appLink} error={error} isLoading={isLoading} locale={locale} os={os} />
    </Modal>
  );
};

const Content = ({ isLoading, error, appLink, locale, os }: ContentProps) => {
  const { t } = useTranslation('orderClaim');
  const {
    color: { danger, success },
  } = useTheme();
  const state = error || 'success';

  const StateIcon = error ? (
    <Icon mb={6} name="ExclamationCircleOutline" size={90} fill={danger} />
  ) : (
    <Icon mb={6} name="CheckCircleOutline" size={90} fill={success} />
  );

  return (
    <ContentWrapper isLoading={isLoading}>
      <SkeletonLoader
        customSkeleton={
          <>
            <rect x="calc((100% - 90px) / 2)" height="90" width="90" rx="5" />
            <rect y="114" height="130px" width="100%" rx="5" />
          </>
        }
        height="220px"
        isLoading={isLoading}
      >
        {StateIcon}
        <Title mb={2} dataTest="order-claim-title" level={1}>
          {t([`${state}.title`, 'error.title'])}
        </Title>

        <Text type="grey" level={3} dataTest="order-claim-subtitle">
          {t([`${state}.subtitle`, 'error.subtitle'])}
        </Text>

        {appLink && <AppStoreButton alt={t('downloadApp')} locale={locale} os={os} url={appLink} />}
      </SkeletonLoader>
    </ContentWrapper>
  );
};

type AppStoreButtonProps = {
  locale: string,
  url: string,
  alt: string,
  os: string | null,
};

const AppStoreButton = ({ locale, url, alt, os }: AppStoreButtonProps) => {
  const [language] = locale?.split('-') ?? ['en'];
  const remappedLocale = language === 'en' ? 'en-us' : locale;

  const src =
    os === 'android'
      ? `https://play.google.com/intl/en_uk/badges/static/images/badges/${language || 'en'}_badge_web_generic.png`
      : `https://toolbox.marketingtools.apple.com/api/v2/badges/download-on-the-app-store/black/${
          remappedLocale || 'en-us'
        }?releaseDate=1680652800`;

  return (
    <StyledLink url={url} target="_blank" rel="noreferrer" dataTest="app-store-button">
      <img height={48} src={src} alt={alt} />
    </StyledLink>
  );
};

const StyledLink = styled(Link)`
  margin-top: ${({ theme }) => theme.margins.xxl};
`;

const ContentWrapper = styled.div<{ isLoading: boolean }>`
  display: flex;
  flex-direction: column;
  justify-content: center;
  ${({ isLoading }) => !isLoading && 'align-items: center;'}
  max-width: 400px;
  height: 100vh;
  margin: 0 auto;
  text-align: center;
`;
